import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

// ─── Realistic leaf with veins — 5 variants ───
export const LeafDetailed = ({ className, delay = 0, variant = 0, color = "hsl(var(--sage))" }: { className?: string; delay?: number; variant?: number; color?: string }) => {
  const paths = [
    // Maple-like leaf
    <>
      <path d="M12 22C12 22 4 16 4 10C4 6 7 2 12 2C17 2 20 6 20 10C20 16 12 22 12 22Z" fill={color} fillOpacity="0.9" />
      <path d="M12 22V6" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
      <path d="M12 8L8 5" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
      <path d="M12 8L16 5" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
      <path d="M12 12L7 10" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
      <path d="M12 12L17 10" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
      <path d="M12 16L9 14" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
      <path d="M12 16L15 14" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
    </>,
    // Elongated leaf with curve
    <>
      <path d="M6 20C6 20 3 12 8 4C10 1 14 1 16 4C21 12 18 20 18 20C18 20 14 18 12 18C10 18 6 20 6 20Z" fill={color} fillOpacity="0.85" />
      <path d="M12 18C12 18 10 12 12 4" stroke={color} strokeWidth="0.7" strokeOpacity="0.45" strokeLinecap="round" />
      <path d="M12 7L9 6" stroke={color} strokeWidth="0.4" strokeOpacity="0.35" />
      <path d="M12 7L15 6" stroke={color} strokeWidth="0.4" strokeOpacity="0.35" />
      <path d="M11 11L8 10" stroke={color} strokeWidth="0.4" strokeOpacity="0.35" />
      <path d="M11 11L14 10" stroke={color} strokeWidth="0.4" strokeOpacity="0.35" />
    </>,
    // Rounded leaf
    <>
      <path d="M12 2C6 2 2 7 2 12C2 17 6 20 12 22C18 20 22 17 22 12C22 7 18 2 12 2Z" fill={color} fillOpacity="0.88" />
      <path d="M12 22V5" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" />
      <path d="M12 8L7 6" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
      <path d="M12 8L17 6" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
      <path d="M12 13L6 11" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
      <path d="M12 13L18 11" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
    </>,
    // Tropical elongated leaf (new)
    <>
      <path d="M12 1C8 3 3 8 2 14C1 18 4 22 8 23C10 23.5 12 22 12 22C12 22 14 23.5 16 23C20 22 23 18 22 14C21 8 16 3 12 1Z" fill={color} fillOpacity="0.82" />
      <path d="M12 2V22" stroke={color} strokeWidth="0.9" strokeOpacity="0.5" strokeLinecap="round" />
      <path d="M12 5Q7 7 4 12" stroke={color} strokeWidth="0.4" strokeOpacity="0.3" fill="none" />
      <path d="M12 5Q17 7 20 12" stroke={color} strokeWidth="0.4" strokeOpacity="0.3" fill="none" />
      <path d="M12 10Q6 13 3 17" stroke={color} strokeWidth="0.4" strokeOpacity="0.25" fill="none" />
      <path d="M12 10Q18 13 21 17" stroke={color} strokeWidth="0.4" strokeOpacity="0.25" fill="none" />
      <path d="M12 15Q8 18 6 21" stroke={color} strokeWidth="0.35" strokeOpacity="0.2" fill="none" />
      <path d="M12 15Q16 18 18 21" stroke={color} strokeWidth="0.35" strokeOpacity="0.2" fill="none" />
    </>,
    // Thin willow-like leaf (new)
    <>
      <path d="M12 1C10 3 7 8 7 12C7 16 10 21 12 23C14 21 17 16 17 12C17 8 14 3 12 1Z" fill={color} fillOpacity="0.8" />
      <path d="M12 2V22" stroke={color} strokeWidth="0.6" strokeOpacity="0.45" strokeLinecap="round" />
      <path d="M12 6L9 8" stroke={color} strokeWidth="0.35" strokeOpacity="0.3" />
      <path d="M12 6L15 8" stroke={color} strokeWidth="0.35" strokeOpacity="0.3" />
      <path d="M12 12L8 14" stroke={color} strokeWidth="0.35" strokeOpacity="0.3" />
      <path d="M12 12L16 14" stroke={color} strokeWidth="0.35" strokeOpacity="0.3" />
    </>,
  ];

  // More organic, slightly random movement per variant
  const swayRange = 3 + (variant % 3) * 2;
  const floatRange = 4 + (variant % 2) * 4;
  const dur = 6 + variant * 2.5 + delay;
  const isMobile = useIsMobile();

  const svg = (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {paths[variant % 5]}
    </svg>
  );

  if (isMobile) {
    return (
      <div className={`absolute z-20 pointer-events-none ${className}`} aria-hidden>
        {svg}
      </div>
    );
  }

  return (
    <motion.div
      className={`absolute z-20 pointer-events-none ${className}`}
      animate={{
        rotate: [-swayRange, swayRange * 0.7, -swayRange * 0.4, swayRange, -swayRange],
        y: [0, -floatRange, floatRange * 0.3, -floatRange * 0.6, 0],
      }}
      transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay }}
    >
      {svg}
    </motion.div>
  );
};

// ─── Branch with leaves ───
export const Branch = ({ className, delay = 0, color = "hsl(var(--sage))", flip = false }: { className?: string; delay?: number; color?: string; flip?: boolean }) => {
  const isMobile = useIsMobile();
  const svg = (
    <svg width="100%" height="100%" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0,40 Q30,38 60,42 Q90,46 120,40" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" strokeOpacity="0.7" />
      <path d="M25,39 Q30,28 35,22" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      <path d="M55,42 Q58,30 62,24" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      <path d="M80,44 Q85,34 90,28" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      <path d="M40,40 Q36,50 32,56" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.4" />
      <path d="M70,43 Q66,54 62,60" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.4" />
      <ellipse cx="35" cy="20" rx="6" ry="10" transform="rotate(-20 35 20)" fill={color} fillOpacity="0.7" />
      <ellipse cx="62" cy="22" rx="5" ry="9" transform="rotate(-10 62 22)" fill={color} fillOpacity="0.65" />
      <ellipse cx="90" cy="26" rx="5" ry="8" transform="rotate(-25 90 26)" fill={color} fillOpacity="0.6" />
      <ellipse cx="32" cy="58" rx="5" ry="8" transform="rotate(15 32 58)" fill={color} fillOpacity="0.55" />
      <ellipse cx="62" cy="62" rx="4" ry="7" transform="rotate(10 62 62)" fill={color} fillOpacity="0.5" />
      <circle cx="36" cy="16" r="2" fill={color} fillOpacity="0.5" />
      <circle cx="91" cy="23" r="1.5" fill={color} fillOpacity="0.45" />
    </svg>
  );

  if (isMobile) {
    return (
      <div
        className={`absolute z-10 pointer-events-none ${className}`}
        style={{ transform: flip ? "scaleX(-1)" : undefined }}
        aria-hidden
      >
        {svg}
      </div>
    );
  }

  return (
    <motion.div
      className={`absolute z-10 pointer-events-none ${className}`}
      style={{ transform: flip ? "scaleX(-1)" : undefined }}
      animate={{ rotate: [-1.5, 2, -0.5, 1.5, -1.5] }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay }}
    >
      {svg}
    </motion.div>
  );
};

// ─── Small twig / tronco ───
export const Twig = ({ className, delay = 0, color = "hsl(var(--olive))" }: { className?: string; delay?: number; color?: string }) => {
  const isMobile = useIsMobile();
  const svg = (
    <svg width="100%" height="100%" viewBox="0 0 40 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 78C20 78 19 50 20 30C21 15 20 4 20 4" stroke={color} strokeWidth="3.5" strokeLinecap="round" fill="none" strokeOpacity="0.65" />
      <path d="M20 20Q12 14 8 10" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      <path d="M20 30Q28 24 34 22" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.45" />
      <path d="M20 45Q10 40 6 36" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" strokeOpacity="0.4" />
      <ellipse cx="8" cy="9" rx="4" ry="6" transform="rotate(-30 8 9)" fill={color} fillOpacity="0.6" />
      <ellipse cx="34" cy="21" rx="3.5" ry="5.5" transform="rotate(20 34 21)" fill={color} fillOpacity="0.55" />
      <ellipse cx="6" cy="35" rx="3" ry="5" transform="rotate(-25 6 35)" fill={color} fillOpacity="0.5" />
    </svg>
  );

  if (isMobile) {
    return <div className={`absolute z-10 pointer-events-none ${className}`} aria-hidden>{svg}</div>;
  }

  return (
    <motion.div
      className={`absolute z-10 pointer-events-none ${className}`}
      animate={{ rotate: [-0.5, 1, -0.5] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay }}
    >
      {svg}
    </motion.div>
  );
};

// ─── Roots for footer ───
export const RootBase = ({ className, color = "hsl(var(--sage-light))" }: { className?: string; color?: string }) => (
  <div className={`absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden h-28 ${className}`}>
    <svg viewBox="0 0 1200 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
      <path d="M0,40 C300,90 600,0 1200,60 L1200,120 L0,120 Z" fill={color} opacity="0.5" />
      <path d="M0,70 C400,10 800,120 1200,80 L1200,120 L0,120 Z" fill={color} opacity="0.7" />
      <path d="M100,55 Q115,80 110,120" stroke={color} strokeWidth="4.5" fill="none" strokeLinecap="round" />
      <path d="M105,70 Q90,85 80,95" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M300,45 Q320,80 310,120" stroke={color} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M305,65 Q285,80 275,95" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M310,80 Q330,100 335,120" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M550,50 Q570,85 560,120" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M555,72 Q535,90 528,105" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M750,42 Q770,90 760,120" stroke={color} strokeWidth="5.5" fill="none" strokeLinecap="round" />
      <path d="M755,68 Q740,85 730,100" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M765,85 Q780,105 785,120" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M950,55 Q965,90 958,120" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M955,75 Q940,95 935,110" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M1100,48 Q1115,85 1108,120" stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round" />
    </svg>
  </div>
);

// ─── Sprout / small plant ───
export const Sprout = ({ className, delay = 0, color = "hsl(var(--olive))" }: { className?: string; delay?: number; color?: string }) => {
  const isMobile = useIsMobile();
  const svg = (
    <svg width="100%" height="100%" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 38V18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeOpacity="0.8" />
      <path d="M16 24C16 24 8 22 6 16C4 10 10 8 16 14" fill={color} fillOpacity="0.8" />
      <path d="M16 18C16 18 24 16 26 10C28 4 22 2 16 8" fill={color} fillOpacity="0.7" />
      <path d="M16 14C16 14 13 10 14 7" stroke={color} strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5" />
    </svg>
  );

  if (isMobile) {
    return <div className={`absolute z-10 pointer-events-none ${className}`} aria-hidden>{svg}</div>;
  }

  return (
    <motion.div
      className={`absolute z-10 pointer-events-none ${className}`}
      animate={{ scale: [1, 1.06, 1], rotate: [0, 3, -2, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay }}
    >
      {svg}
    </motion.div>
  );
};

// ─── Seeds ───
export const Seeds = ({ className, color = "hsl(var(--sand))" }: { className?: string; color?: string }) => (
  <div className={`absolute pointer-events-none ${className}`}>
    <svg width="100%" height="100%" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="12" cy="20" rx="4" ry="6" transform="rotate(45 12 20)" fill={color} fillOpacity="0.7" />
      <ellipse cx="35" cy="12" rx="3.5" ry="5" transform="rotate(15 35 12)" fill={color} fillOpacity="0.6" />
      <ellipse cx="58" cy="28" rx="4.5" ry="7" transform="rotate(-30 58 28)" fill={color} fillOpacity="0.65" />
      <ellipse cx="22" cy="42" rx="3" ry="4.5" transform="rotate(60 22 42)" fill={color} fillOpacity="0.55" />
      <ellipse cx="50" cy="48" rx="3" ry="5" transform="rotate(-15 50 48)" fill={color} fillOpacity="0.5" />
      <path d="M35 12 Q36 6 38 3" stroke="hsl(var(--sage))" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5" />
      <path d="M58 28 Q60 22 63 19" stroke="hsl(var(--sage))" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4" />
    </svg>
  </div>
);

// ─── Organic section divider (vine) ───
export const VineDivider = ({ className, color = "hsl(var(--sage))" }: { className?: string; color?: string }) => (
  <div className={`w-full pointer-events-none overflow-hidden h-12 ${className}`}>
    <svg viewBox="0 0 1200 50" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
      <path d="M0,25 C200,10 400,40 600,25 C800,10 1000,40 1200,25" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" strokeOpacity="0.4" />
      <ellipse cx="200" cy="18" rx="8" ry="4" transform="rotate(-20 200 18)" fill={color} fillOpacity="0.45" />
      <ellipse cx="450" cy="32" rx="6" ry="3" transform="rotate(15 450 32)" fill={color} fillOpacity="0.4" />
      <ellipse cx="700" cy="16" rx="7" ry="3.5" transform="rotate(-10 700 16)" fill={color} fillOpacity="0.45" />
      <ellipse cx="950" cy="34" rx="6" ry="3" transform="rotate(20 950 34)" fill={color} fillOpacity="0.4" />
      {/* Extra tiny buds on vine */}
      <circle cx="320" cy="22" r="2" fill={color} fillOpacity="0.35" />
      <circle cx="580" cy="28" r="1.8" fill={color} fillOpacity="0.3" />
      <circle cx="830" cy="20" r="2.2" fill={color} fillOpacity="0.35" />
    </svg>
  </div>
);

// ─── Organic Hills background ───
export const OrganicHills = ({ className, color = "hsl(var(--sage-light))" }: { className?: string; color?: string }) => (
  <div className={`absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden h-32 ${className}`}>
    <svg viewBox="0 0 1200 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
      <path d="M0,60 C400,120 800,0 1200,60 L1200,120 L0,120 Z" fill={color} />
      <path d="M0,90 C300,30 900,140 1200,90 L1200,120 L0,120 Z" fill={color} opacity="0.5" />
    </svg>
  </div>
);

// ─── Footer Roots (rich version) ───
export const FooterRoots = ({ color = "hsl(var(--sage))" }: { color?: string }) => (
  <div className="absolute top-0 left-0 w-full pointer-events-none overflow-hidden h-20 -translate-y-full">
    <svg viewBox="0 0 1200 80" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
      <path d="M0,78 L1200,78" stroke={color} strokeWidth="2" strokeOpacity="0.15" />
      <path d="M150,0 Q155,25 148,45 Q140,60 145,80" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" strokeOpacity="0.25" />
      <path d="M148,30 Q130,40 120,50" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.18" />
      <path d="M400,0 Q410,30 405,55 Q398,70 402,80" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" strokeOpacity="0.2" />
      <path d="M405,40 Q420,52 430,60" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" strokeOpacity="0.15" />
      <path d="M403,55 Q385,65 378,75" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.12" />
      <path d="M700,0 Q695,20 700,45 Q705,65 698,80" stroke={color} strokeWidth="3.5" strokeLinecap="round" fill="none" strokeOpacity="0.22" />
      <path d="M700,35 Q680,48 670,58" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" strokeOpacity="0.15" />
      <path d="M950,0 Q955,28 950,50 Q945,68 950,80" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" strokeOpacity="0.2" />
      <path d="M950,42 Q968,55 975,65" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" strokeOpacity="0.15" />
    </svg>
  </div>
);

// ─── Small moss / undergrowth cluster ───
export const MossCluster = ({ className, color = "hsl(var(--sage))" }: { className?: string; color?: string }) => (
  <div className={`absolute pointer-events-none z-10 ${className}`}>
    <svg width="100%" height="100%" viewBox="0 0 60 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="10" cy="24" rx="8" ry="4" fill={color} fillOpacity="0.5" />
      <ellipse cx="28" cy="22" rx="10" ry="5" fill={color} fillOpacity="0.6" />
      <ellipse cx="48" cy="25" rx="7" ry="3.5" fill={color} fillOpacity="0.45" />
      {/* Tiny blades */}
      <path d="M10 24Q8 16 6 12" stroke={color} strokeWidth="1" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      <path d="M14 23Q16 14 18 10" stroke={color} strokeWidth="0.8" strokeLinecap="round" fill="none" strokeOpacity="0.45" />
      <path d="M28 22Q26 12 24 8" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" strokeOpacity="0.55" />
      <path d="M32 21Q34 13 36 9" stroke={color} strokeWidth="0.9" strokeLinecap="round" fill="none" strokeOpacity="0.45" />
      <path d="M48 25Q46 18 44 14" stroke={color} strokeWidth="0.8" strokeLinecap="round" fill="none" strokeOpacity="0.4" />
    </svg>
  </div>
);

// Keep old LeafSolid for backward compat
export const LeafSolid = LeafDetailed;
